import { useState } from "react";
import { Link } from "react-router-dom";
import MediaCard from "@/components/feature/MediaCard";
import { getRelatedMedia, typeLabel, type Media } from "@/mocks/media";

const infoRows = (media: Media) => [
  { label: "导演", value: media.director },
  { label: "主演", value: media.cast.join(" / ") },
  { label: "年份", value: String(media.year) },
  { label: "类型", value: media.genres.join(" / ") },
  { label: "地区", value: media.region },
  { label: "时长", value: media.duration },
  { label: "集数", value: media.episodes },
  { label: "更新日期", value: media.updatedAt },
];

export default function DetailContent({ media }: { media: Media }) {
  const [toast, setToast] = useState("");
  const related = getRelatedMedia(media, 6);
  const sameCategory = related.slice(0, 4);

  const showToast = (message: string) => {
    setToast(message);
    window.setTimeout(() => setToast(""), 2200);
  };

  const handleShare = async () => {
    const basePath = __BASE_PATH__.replace(/\/$/, "");
    const shareUrl = `${window.location.origin}${basePath}/detail/${media.id}`;
    try {
      await navigator.clipboard.writeText(shareUrl);
      showToast("链接已复制到剪贴板");
    } catch {
      showToast(`分享链接：${shareUrl}`);
    }
  };

  return (
    <>
      <div className="w-full bg-primary-900">
        <div className="w-full px-4 md:px-6 xl:px-10 py-6 md:py-10">
          <nav aria-label="面包屑" className="flex items-center flex-wrap gap-1.5 text-xs text-background-300 mb-6">
            <Link to="/" className="hover:text-secondary-300 transition-colors">首页</Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to={`/category/${media.type}`} className="hover:text-secondary-300 transition-colors">
              {typeLabel[media.type]}
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-secondary-400">{media.title}</span>
          </nav>

          <div className="flex flex-col md:flex-row gap-6 md:gap-9">
            <div className="w-full max-w-[220px] md:w-[236px] shrink-0 mx-auto md:mx-0">
              <div className="w-full aspect-[6/8.4] rounded-lg overflow-hidden border border-background-50/15 bg-background-800">
                <img
                  src={media.cover}
                  alt={`${media.title} 大幅封面`}
                  title={`${media.title} ${typeLabel[media.type]} 海报`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="mt-3 flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleShare}
                  className="flex-1 h-10 inline-flex items-center justify-center gap-1.5 rounded-md bg-accent-500 text-background-50 text-sm font-medium hover:bg-accent-600 transition-colors whitespace-nowrap"
                >
                  <i className="ri-share-forward-line"></i>
                  分享
                </button>
                <Link
                  to={`/category/${media.type}`}
                  className="h-10 px-4 inline-flex items-center justify-center rounded-md border border-background-50/30 text-background-100 text-sm hover:bg-background-50/10 transition-colors whitespace-nowrap"
                >
                  同频道
                </Link>
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <span className="px-2.5 h-6 inline-flex items-center rounded-md bg-secondary-500 text-foreground-950 text-xs font-bold">
                  <i className="ri-star-fill mr-1 text-[10px]"></i>
                  {media.rating.toFixed(1)}
                </span>
                <span className="px-2.5 h-6 inline-flex items-center rounded-md bg-accent-500 text-background-50 text-xs">
                  {typeLabel[media.type]}
                </span>
                <span className="text-xs text-background-300">{media.status}</span>
                <span className="text-xs text-background-300">·</span>
                <span className="text-xs text-background-300">{(media.views / 10000).toFixed(0)}万次浏览</span>
              </div>

              <h1 className="font-heading text-2xl md:text-4xl font-bold text-background-50 leading-tight">
                {media.title}
              </h1>

              <div className="mt-4 flex flex-wrap gap-1.5">
                {media.type === "variety" ? (
                  <span className="px-2.5 h-7 inline-flex items-center rounded-md bg-primary-800 text-background-100 text-xs">
                    {media.episodes}
                  </span>
                ) : (
                  media.genres.map((genre) => (
                    <Link
                      key={genre}
                      to={`/category/${media.type}`}
                      className="px-2.5 h-7 inline-flex items-center rounded-md bg-primary-800 text-background-100 text-xs hover:bg-secondary-500 hover:text-foreground-950 transition-colors"
                    >
                      {genre}
                    </Link>
                  ))
                )}
              </div>

              <p className="mt-4 text-sm leading-7 text-background-200 max-w-3xl">{media.summary}</p>

              <div className="mt-4 rounded-md border border-primary-700 bg-primary-950/40 px-3.5 py-2.5 inline-flex items-center gap-2 max-w-full">
                <i className="ri-information-line text-accent-400 shrink-0"></i>
                <span className="text-xs text-background-300 line-clamp-1">
                  本站为示例演示站点，不提供真实影片播放，作品信息均为虚构内容
                </span>
              </div>

              <dl className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2.5 max-w-2xl">
                {infoRows(media).map((row) => (
                  <div key={row.label} className="flex items-start gap-3 text-sm">
                    <dt className="text-background-400 shrink-0 w-16">{row.label}</dt>
                    <dd className="text-background-100 min-w-0 break-words">{row.value}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </div>
        </div>
      </div>

      <div className="w-full px-4 md:px-6 xl:px-10 py-8 md:py-10">
        <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
          <section aria-label="剧情简介" className="flex-1 min-w-0">
            <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-950 flex items-center gap-2">
              <span className="w-1 h-5 rounded-full bg-accent-500"></span>
              剧情简介
            </h2>
            <div className="mt-3 rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
              <p className="text-sm leading-7 text-foreground-700">{media.summary}</p>
              <p className="text-sm leading-7 text-foreground-600 mt-3">
                本页作品信息为示例演示内容，剧中人物、导演与演员均为虚构设定，仅用于展示影视详情页的结构与浏览体验，
                不提供真实播放地址。
              </p>
              <div className="mt-4 pt-4 border-t border-background-200 flex flex-wrap items-center gap-3">
                <span className="text-xs text-foreground-500">所属分类：</span>
                {[
                  { label: media.type === "movie" ? "电影" : media.type === "tv" ? "电视剧" : "综艺", to: `/category/${media.type}` },
                  { label: media.region, to: `/category/${media.type}` },
                  { label: String(media.year), to: `/category/${media.type}` },
                ].map((chip) => (
                  <Link
                    key={chip.label}
                    to={chip.to}
                    className="px-2.5 h-7 inline-flex items-center rounded-md bg-secondary-100 text-secondary-900 text-xs hover:bg-secondary-200 transition-colors"
                  >
                    #{chip.label}
                  </Link>
                ))}
              </div>
            </div>

            <section aria-label="同类型推荐" className="mt-8">
              <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-950 flex items-center gap-2">
                <span className="w-1 h-5 rounded-full bg-primary-500"></span>
                你可能还喜欢
              </h2>
              <p className="text-xs text-foreground-500 mt-1.5">根据类型与地区相似度推荐</p>
              <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
                {sameCategory.map((item) => (
                  <MediaCard key={item.id} media={item} />
                ))}
              </div>
            </section>
          </section>

          <aside className="w-full lg:w-[300px] shrink-0">
            <div className="rounded-lg border border-background-200 bg-background-50 overflow-hidden">
              <div className="flex items-center justify-between px-4 h-12 border-b border-background-200 bg-background-100">
                <h2 className="font-heading text-[15px] font-semibold text-foreground-950 flex items-center gap-2">
                  <i className="ri-thumb-up-line text-accent-500"></i>
                  相关推荐
                </h2>
                <span className="text-[11px] text-foreground-500">同类优先</span>
              </div>
              <div className="flex lg:flex-col gap-3 overflow-x-auto lg:overflow-visible scrollbar-hide px-3 py-3">
                {related.map((item) => (
                  <Link
                    key={item.id}
                    to={`/detail/${item.id}`}
                    className="w-[212px] lg:w-auto shrink-0 flex items-center gap-3 group"
                  >
                    <div className="w-12 h-16 shrink-0 rounded overflow-hidden bg-background-200">
                      <img
                        src={item.cover}
                        alt={`${item.title} 推荐封面`}
                        title={`${item.title} ${typeLabel[item.type]}`}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                    <div className="min-w-0">
                      <p className="text-[13px] font-medium text-foreground-950 truncate group-hover:text-primary-600 transition-colors">
                        {item.title}
                      </p>
                      <p className="text-[11px] text-foreground-500 mt-1 truncate">
                        {item.year} · {item.genres[0]}
                      </p>
                      <p className="text-[11px] text-accent-600 mt-0.5">评分 {item.rating.toFixed(1)}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>

            <div className="mt-4 rounded-lg bg-background-100 border border-background-200 p-4">
              <h3 className="font-heading text-sm font-semibold text-foreground-950">找不到想看的？</h3>
              <p className="text-xs leading-6 text-foreground-600 mt-1.5">
                试试用关键词搜索片名、类型或地区，也可以直接浏览全部作品列表。
              </p>
              <div className="mt-3 flex flex-col gap-2">
                <Link
                  to="/search"
                  className="h-9 inline-flex items-center justify-center rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
                >
                  <i className="ri-search-line mr-1.5"></i>
                  去搜索
                </Link>
                <Link
                  to="/"
                  className="h-9 inline-flex items-center justify-center rounded-md border border-background-300 text-foreground-700 text-sm hover:border-primary-300 hover:text-primary-600 transition-colors whitespace-nowrap"
                >
                  返回首页
                </Link>
              </div>
            </div>
          </aside>
        </div>
      </div>

      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 px-4 h-10 inline-flex items-center rounded-md bg-foreground-950 text-background-50 text-sm animate-fade-up max-w-[90vw]">
          <i className="ri-checkbox-circle-line mr-1.5"></i>
          <span className="truncate">{toast}</span>
        </div>
      )}
    </>
  );
}